"use client";

/**
 * Records clicks on links to demo / pricing / contact / sign-up / the free tools, and
 * mail / phone links, anywhere on the marketing site -- the fallback for every link
 * that has no tracker of its own. What gets recorded is decided by
 * siteLinkClickEvent() in src/lib/siteLinks.ts.
 *
 * Bubble phase, not capture: by the time a click reaches document, every React
 * onClick for it has run, so a link that an explicit handler (the header menus, the
 * feature-page hero) or the banner listener already recorded is skipped via
 * wasClickTracked() rather than counted twice. defaultPrevented is not checked: Next's
 * <Link> prevents the default to do a client-side navigation, and that is still a click.
 */

import { useEffect } from "react";
import { siteLinkClickEvent, type LinkRegion } from "@/lib/siteLinks";
import { trackCtaClick, wasClickTracked } from "@/utils/ctaTracking";

/** data-site-header marks the fixed header (nav + urgency banner) and the phone menu. */
function regionOf(el: Element): LinkRegion {
  if (el.closest("footer")) return "footer";
  if (el.closest("[data-site-header]")) return "header";
  return "page";
}

export function useSiteLinkTracking(): void {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wasClickTracked()) return;
      const el = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!el) return;

      const event = siteLinkClickEvent({
        href: el.getAttribute("href") || "",
        text: el.textContent || "",
        region: regionOf(el),
        origin: window.location.origin,
      });
      if (!event) return;

      trackCtaClick(event.destination, event.label, undefined, {
        section: event.section,
        location: event.location,
      });
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
}
